import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { minutesSinceOperationsTime } from '@/lib/datetime'
import { queryKeys } from '@/lib/query-client'
import type { AdminTrip, TripFilters } from '@/types/api'
import { fetchTrips } from './api'

/**
 * The trips whose scheduled moment has come and gone while their status stayed
 * where it was.
 *
 * A supervisor who forgets to press Start leaves a confirmed trip sitting in the
 * queue: the driver is out driving, the dashboard says the journey has not
 * begun, and every figure built on trip status — the active-trip count, a
 * driver's workload, the day's completions — is wrong until somebody notices.
 * This finds those rows so a screen can put them in front of the operator
 * instead of waiting to be searched for.
 */

export type DueAction = 'start' | 'end'

export interface DueTrip {
  /** Unique per trip *and* per transition — one trip falls due twice, hours apart. */
  key: string
  trip: AdminTrip
  action: DueAction
  /** The scheduled time that has passed: the start for `start`, the end for `end`. */
  dueAt: string
  /** How long ago it passed, in whole minutes. Zero for the minute it falls due. */
  overdueMinutes: number
}

/**
 * The statuses a trip can still be started from, mirroring `AdminTripDTO.STARTABLE`
 * on the server. It narrows what is fetched and nothing more — the `canStart`
 * flag on each row is what actually decides whether the button appears, so if
 * these two ever drift the dashboard offers fewer actions rather than one the
 * API would refuse.
 */
const STARTABLE_STATUSES = 'PENDING,CONFIRMED,ON_THE_WAY,ARRIVED'

/** A trip can only be ended once it is actually running. */
const ENDABLE_STATUSES = 'STARTED'

/**
 * Two separate reads rather than one over both status sets.
 *
 * A single page ordered by start time would be filled by the oldest startable
 * trips — precisely the backlog this exists to surface — and the running trips
 * that need ending would fall off the end of it. Each set gets its own page so
 * neither can crowd out the other.
 */
const DUE_PAGE_SIZE = 50

/** How often the lists are re-read, for an operator who leaves the screen open. */
const POLL_MS = 60_000

/** How often "is it due yet?" is re-asked between reads. */
const TICK_MS = 30_000

function dueFilters(status: string): TripFilters {
  // Oldest first: the trip that has been waiting longest is the one that most
  // needs pressing, and it keeps the overdue rows on the first page.
  return { page: 0, size: DUE_PAGE_SIZE, sort: 'startTime', direction: 'asc', status }
}

export interface UseDueTripsResult {
  /** Most overdue first. */
  dueTrips: DueTrip[]
}

export function useDueTrips(): UseDueTripsResult {
  const now = useClockTick(TICK_MS)

  const startFilters = useMemo(() => dueFilters(STARTABLE_STATUSES), [])
  const endFilters = useMemo(() => dueFilters(ENDABLE_STATUSES), [])

  // Both share the trips list key family, so starting or ending a trip anywhere
  // in the dashboard refreshes these too.
  const startableQuery = useQuery({
    queryKey: queryKeys.trips.list(startFilters),
    queryFn: () => fetchTrips(startFilters),
    refetchInterval: POLL_MS,
  })

  const endableQuery = useQuery({
    queryKey: queryKeys.trips.list(endFilters),
    queryFn: () => fetchTrips(endFilters),
    refetchInterval: POLL_MS,
  })

  const startable = startableQuery.data?.content
  const endable = endableQuery.data?.content

  const dueTrips = useMemo(() => {
    const due: DueTrip[] = []

    for (const trip of startable ?? []) {
      collect(due, trip, 'start', trip.startTime, trip.canStart, now)
    }
    for (const trip of endable ?? []) {
      // An open-ended TRAVEL booking has no end time and so can never fall due
      // for one; it is ended when the journey actually finishes.
      collect(due, trip, 'end', trip.endTime, trip.canEnd, now)
    }

    return due.sort((first, second) => second.overdueMinutes - first.overdueMinutes)
  }, [startable, endable, now])

  return { dueTrips }
}

function collect(
  into: DueTrip[],
  trip: AdminTrip,
  action: DueAction,
  dueAt: string | null,
  permitted: boolean,
  now: number,
): void {
  if (!permitted || !dueAt) return

  const overdueMinutes = minutesSinceOperationsTime(dueAt, now)
  if (overdueMinutes === null || overdueMinutes < 0) return

  into.push({ key: `${action}-${trip.id}`, trip, action, dueAt, overdueMinutes })
}

/**
 * A value that changes on a timer, so a trip becomes due while the screen is
 * open rather than only when it is next loaded. Cheaper than refetching: the
 * answer depends on the clock, not on the server.
 */
function useClockTick(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(timer)
  }, [intervalMs])

  return now
}
